/**
 * Static config for the CwC "The UK's best value recipe box" price-comparison section. Copy and all
 * figures are verified against Figma (section VALUE, 320:11418) and colocated here (mirrors
 * `CWCHowItWorksSection/config.ts`) so content and competitor numbers are updatable in one place —
 * no hardcoded strings in the component.
 *
 * Gousto's price-per-meal is a **static value for this story**; sourcing it from the live marketing
 * pricing config (`useMarketingPricingConfig`) is out of scope (tracked at B20-1193).
 *
 * The competitor comparison claims are signed off by legal/compliance as part of the **design**
 * approval (product/design take the designs to legal) — provenance is not tracked in code. So the
 * figures here just mirror the approved design; keep them in sync when the design updates.
 */
export type CWCPriceComparisonBrand = {
  name: string
  /** Highlights this brand's column + shows the "Best value" callout. Gousto only. */
  bestValue?: boolean
  pricePerMeal: string
  weeklyRecipes: string
  readyMeals: boolean
  customiseRecipes: boolean
}

export const CWC_PRICE_COMPARISON_SECTION: {
  heading: string
  body: string
  brands: CWCPriceComparisonBrand[]
  cta: { label: string; href: string }
} = {
  heading: "The UK's best value recipe box",
  body: 'Your Gousto box beats HelloFresh on price every time, plus more recipes to choose from each week.',
  brands: [
    {
      name: 'Gousto',
      bestValue: true,
      pricePerMeal: '£3.20',
      weeklyRecipes: '175+',
      readyMeals: true,
      customiseRecipes: true,
    },
    {
      name: 'HelloFresh',
      pricePerMeal: '£3.65',
      weeklyRecipes: '80+',
      readyMeals: false,
      customiseRecipes: true,
    },
    {
      name: 'Simmer',
      pricePerMeal: '£7.49',
      weeklyRecipes: '24+',
      readyMeals: true,
      customiseRecipes: false,
    },
  ],
  cta: {
    label: 'See our box prices',
    href: '/box-prices',
  },
}

/** Dimension rows, in display order. `key` selects the value off each brand record. */
export type CWCPriceComparisonDimension = {
  label: string
  key: keyof Pick<
    CWCPriceComparisonBrand,
    'pricePerMeal' | 'weeklyRecipes' | 'readyMeals' | 'customiseRecipes'
  >
  type: 'value' | 'yesNo'
}

export const CWC_PRICE_COMPARISON_DIMENSIONS: CWCPriceComparisonDimension[] = [
  // Non-breaking space keeps "per meal" together, so the narrow mobile column wraps as
  // "Price" / "per meal" rather than "Price per" / "meal" (design sign-off). Accessible name
  // still normalises to "Price per meal".
  { label: 'Price per meal', key: 'pricePerMeal', type: 'value' },
  { label: 'Weekly recipes', key: 'weeklyRecipes', type: 'value' },
  { label: 'Ready meals', key: 'readyMeals', type: 'yesNo' },
  { label: 'Customise recipes', key: 'customiseRecipes', type: 'yesNo' },
]
